// Le rail : la navigation fixe du produit (Figma « App Compta — Écrans »,
// composant Rail). Six modules transverses, la cloche de notifications avec
// son point rouge quand il reste du non-lu, et le compte (pas d'aide : retirée
// le 16/10, elle ne menait nulle part). Deux états
// animés : replié 68px (icônes seules, barre d'indicateur) et déplié 204px.

import "./rail.css";

import type { ComptaIconeNom } from "@app/components/compta/ui/ComptaIcone";
import { ComptaIcone } from "@app/components/compta/ui/ComptaIcone";
import { MarqueLinea } from "@app/components/compta/ui/MarqueLinea";
import { SIDEBAR_BRAND_NAME } from "@app/lib/compta/branding";

export type ComptaModule =
  | "portefeuille"
  | "echeancier"
  | "relances"
  | "tableaux-de-bord"
  | "clients"
  | "parametres";

interface ModuleDuRail {
  id: ComptaModule;
  libelle: string;
  icone: ComptaIconeNom;
  iconeActive: ComptaIconeNom;
}

const MODULES: ModuleDuRail[] = [
  {
    id: "portefeuille",
    libelle: "Portefeuille",
    icone: "squares-four",
    iconeActive: "squares-four-fill",
  },
  {
    id: "echeancier",
    libelle: "Échéancier",
    icone: "clock-clockwise",
    iconeActive: "clock-clockwise-fill",
  },
  {
    id: "relances",
    libelle: "Relances",
    icone: "paper-plane-tilt",
    iconeActive: "paper-plane-tilt-fill",
  },
  {
    id: "tableaux-de-bord",
    libelle: "Tableaux de bord",
    icone: "gauge",
    iconeActive: "gauge-fill",
  },
  {
    id: "clients",
    libelle: "Clients",
    icone: "users",
    iconeActive: "users-fill",
  },
  {
    id: "parametres",
    libelle: "Paramètres",
    icone: "gear-six",
    iconeActive: "gear-six-fill",
  },
];

interface ComptaRailProps {
  actif: ComptaModule;
  deplie?: boolean;
  // Notifications non lues : le point rouge n'apparaît que si > 0.
  nonLues?: number;
  initiales: string;
  nom?: string;
  // Les lignes de contexte du cabinet, affichées dépliées au-dessus du
  // compte (« 2 pièces attendues chez 2 clients »…).
  contexte?: string[];
  // Les modules pas encore disponibles : grisés, « Bientôt » au survol, inertes.
  indisponibles?: ComptaModule[];
  // Le clic sur la marque replie ou déplie le rail.
  onBasculer?: () => void;
  onNaviguer?: (module: ComptaModule) => void;
  onNotifications?: () => void;
  onCompte?: () => void;
}

export function ComptaRail({
  actif,
  deplie = false,
  nonLues = 0,
  initiales,
  nom,
  contexte,
  indisponibles,
  onBasculer,
  onNaviguer,
  onNotifications,
  onCompte,
}: ComptaRailProps) {
  const Marque = onBasculer ? "button" : "div";
  return (
    <nav
      className={deplie ? "crail crail--deplie" : "crail"}
      aria-label="Modules"
    >
      <Marque
        {...(onBasculer
          ? {
              type: "button" as const,
              onClick: onBasculer,
              "aria-label": deplie ? "Replier le rail" : "Déplier le rail",
            }
          : {})}
        className={
          onBasculer ? "crail__marque crail__marque--bouton" : "crail__marque"
        }
      >
        <MarqueLinea taille={26} className="crail__symbole" />
        {deplie && <span className="crail__nom">{SIDEBAR_BRAND_NAME}</span>}
      </Marque>

      {MODULES.map((module) => {
        const estActif = module.id === actif;
        const bientot = indisponibles?.includes(module.id) ?? false;
        const classes = ["crail__item"];
        if (estActif) {
          classes.push("crail__item--actif");
        }
        if (bientot) {
          classes.push("crail__item--bientot");
        }
        return (
          <button
            key={module.id}
            type="button"
            className={classes.join(" ")}
            aria-current={estActif ? "page" : undefined}
            aria-label={deplie ? undefined : module.libelle}
            aria-disabled={bientot ? true : undefined}
            title={bientot ? `${module.libelle} · bientôt` : undefined}
            onClick={bientot ? undefined : () => onNaviguer?.(module.id)}
          >
            {estActif && <span className="crail__indicateur" />}
            <ComptaIcone
              nom={estActif ? module.iconeActive : module.icone}
              taille={23}
            />
            {deplie && <span className="crail__libelle">{module.libelle}</span>}
          </button>
        );
      })}

      <div className="crail__ressort" />

      <button
        type="button"
        className="crail__item"
        aria-label={
          nonLues > 0 ? `Notifications, ${nonLues} non lues` : "Notifications"
        }
        onClick={onNotifications}
      >
        <span className="crail__cloche">
          <ComptaIcone nom="bell" taille={23} />
          {nonLues > 0 && <span className="crail__badge" />}
        </span>
        {deplie && <span className="crail__libelle">Notifications</span>}
      </button>

      {deplie && contexte && contexte.length > 0 && (
        <div className="crail__contexte">
          {contexte.map((ligne) => (
            <div key={ligne}>{ligne}</div>
          ))}
        </div>
      )}

      <button
        type="button"
        className="crail__compte"
        aria-label="Compte"
        onClick={onCompte}
      >
        <span className="crail__avatar">{initiales}</span>
        {deplie && nom && <span className="crail__libelle">{nom}</span>}
      </button>
    </nav>
  );
}
